import React from "react";
import LegalLayout from "./LegalLayout";
import { usePageMeta } from "@/lib/usePageMeta";

export default function CookiePolicy() {
  usePageMeta(
    "Cookie Policy — Leadmato",
    "How Leadmato uses cookies and similar technologies — a plain-English rundown of what we store in your browser and why.",
  );
  return (
    <LegalLayout title="Cookie Policy" updated="July 2026">
      <p>
        This Cookie Policy explains how <strong>Win Massive Impact Pvt. Ltd.</strong> ("Leadmato",
        "we", "us") uses cookies and similar technologies when you visit
        <a href="https://www.leadmato.com"> leadmato.com</a> or use the Leadmato application. Read
        it alongside our <a href="/privacy-policy">Privacy Policy</a>.
      </p>

      <h2>1. What are cookies?</h2>
      <p>
        Cookies are small text files a website stores in your browser. They let a site remember
        who you are between requests, keep you logged in, and remember your preferences. Similar
        technologies — like <code>localStorage</code> and <code>sessionStorage</code> — do the same
        job for the app.
      </p>

      <h2>2. Cookies we set</h2>
      <p>Most cookies Leadmato sets are strictly necessary or functional. We also run one advertising/analytics cookie via the Meta (Facebook) Pixel — see section 4.</p>
      <table>
        <thead>
          <tr><th>Cookie</th><th>Purpose</th><th>Duration</th></tr>
        </thead>
        <tbody>
          <tr><td><code>session_token</code></td><td>Keeps you signed in to the Leadmato app.</td><td>7 days</td></tr>
          <tr><td><code>admin_session_token</code></td><td>Signs administrators into the internal control room. Only set on <code>/admin</code>.</td><td>7 days</td></tr>
          <tr><td><code>lm_theme</code></td><td>Remembers your interface preferences (light / dark).</td><td>1 year</td></tr>
          <tr><td><code>_fbp</code></td><td>Set by the Meta (Facebook) Pixel to measure sign-ups and support advertising. See section 4.</td><td>3 months</td></tr>
        </tbody>
      </table>

      <h2>3. Local storage we use</h2>
      <ul>
        <li><code>lm_admin_token</code> — a fallback token to talk to the admin API. Only present if you're a Leadmato administrator.</li>
        <li>Small onboarding flags (e.g. "you've seen the welcome tour") to avoid nagging returning users.</li>
      </ul>

      <h2>4. Analytics & advertising (Meta Pixel)</h2>
      <p>
        We run the <strong>Meta (Facebook) Pixel</strong> on our marketing and app pages. It records
        a page view when you load a page, and a registration event when you finish signing up, so we
        can measure how well our advertising reaches new customers. To do this the pixel may set the
        <code> _fbp</code> cookie and send event data to Meta, which can use it for ad measurement and
        audience retargeting.
      </p>
      <p>
        You can prevent this by blocking cookies in your browser (see section 6), by using a content/ad
        blocker, or through Meta's own "off-Facebook activity" controls in your Meta account. We never
        sell your personal data.
      </p>

      <h2>5. Payments</h2>
      <p>
        When you pay for a plan, Razorpay may set its own strictly-necessary cookies on the
        checkout page in accordance with{" "}
        <a href="https://razorpay.com/privacy/" target="_blank" rel="noreferrer">
          their privacy policy
        </a>. These cookies never appear on the Leadmato marketing site.
      </p>

      <h2>6. Managing cookies</h2>
      <p>
        You can delete or block cookies from your browser at any time. Blocking strictly-necessary
        cookies may prevent parts of the app from working — for instance, you'll be signed out.
      </p>

      <h2>7. Changes to this policy</h2>
      <p>
        If we materially change what cookies we set, we'll bump the "Last updated" date at the top
        of this page and, where required, notify signed-in users inside the app.
      </p>

      <h2>8. Contact</h2>
      <p>
        Questions about this policy? Email us at{" "}
        <a href="mailto:support@aifreedomclub.com">support@aifreedomclub.com</a> or visit our{" "}
        <a href="/contact">contact page</a>.
      </p>
    </LegalLayout>
  );
}
